import { useTheme } from '@chronosphereio/chrono-ui';
import { produce } from 'immer';
import { useMemo } from 'react';
import {
  isLegendItemSelected,
  LegendItem,
  SelectedLegendItemState,
  useChartsTheme,
  useSeriesColorGenerator,
} from '@/components';
import {
  GetSeriesById,
  getCalculations,
  getOverriddenProperties,
  QueryMetadata,
  SeriesByIdMapValue,
  THRESHOLD_SERIES_NAME_PREFIX,
  ThresholdOptions,
  TIME_SERIES_SPARSE_SERIES_RENDER_LIMIT,
  TimeChartSeriesMapping,
  TimeSeriesChartVisualOptions,
  TimeSeriesChartYAxisOptions,
  TimeSeriesDataset,
  TimeSeriesOverrides,
  TimeSeriesValueTuple,
  TimeSeriesVisualOverride,
  TimeSeriesWithMapping,
  TimeSeriesWithoutValues,
} from '@/core';
import { DIFF_BAND_TRUNCATION_SERIES_NAME } from '@/core/utils/truncation';
import { DEFAULT_UNIT, TimeSeriesChartProps, TimeSeriesGroupByProps } from '../time-series-chart-model';
import { getThresholdsSeries, getTimeSeries } from '../utils/data-transform';
import { TimeSeriesGroup } from './dataset';
import { UseTimeSeriesQuickFilterResponse } from './use-quick-filter';

const EMPTY_NONCONNECTED_DATAPOINTS: TimeSeriesValueTuple[] = [];
const EMPTY_SERIES_MAPPING: TimeChartSeriesMapping = [];
const EMPTY_LEGEND_ITEMS: LegendItem[] = [];

export interface TimeSeriesConfigParams
  extends Pick<
    TimeSeriesChartProps,
    'enableLabelsToColumns' | 'legend' | 'getCustomLegendItemData' | 'barCategoryGap'
  > {
  /**
   * We loop through queryResults in TimeSeriesChartPanelProcessing and output this dataset that includes
   * custom series metadata like color and compare mode properties. From this we can derive valid ECharts
   * series options and required config for our custom tooltip and legend.
   */
  timeSeriesDataset: TimeSeriesDataset[];
  /**
   * Total number of series for all query results. Used for performance optimizations (see optimized-mode.ts).
   */
  totalSeries: number;
  /**
   * Total number of data points for all query results. Used for performance optimizations (see optimized-mode.ts).
   */
  totalDatapoints: number;
  /**
   * Whether optimized mode for TIME_SERIES_SPARSE_SERIES_RENDER_LIMIT is active.
   * Impacts hiddenDataPointCount calculation.
   */
  shouldShowAllDataPoints: boolean;
  /**
   * Visual options to control chart appearance (line width, color palette, etc).
   */
  visual: TimeSeriesChartVisualOptions;
  /**
   * User can hide and show series from the legend. Managed in useSelectedLegendItems.
   */
  selectedLegendItems: SelectedLegendItemState;
  /**
   * Quick filter used in legend search to show and hide series (when showQuickFilter is true).
   */
  quickFilter: string;
  /**
   * Controls how thresholds are displayed (render using markLine or markArea).
   */
  thresholds?: ThresholdOptions;
  /**
   * Series overrides for more control over appearance (color, line style, etc).
   */
  overrides?: TimeSeriesOverrides;
  /**
   * Our backend truncation limit which is used for additional performance optimizations like
   * changing zlevel to allow multiple canvas instances per chart.
   */
  queryTruncationSeriesLimit?: number;
  /**
   * Whether to apply additional processing when log yAxis is used.
   */
  isAxisLogScaleMapping?: boolean[];
  /**
   * Left yAxis options, this is our default axis used for all series unless overridden.
   */
  yAxis?: TimeSeriesChartYAxisOptions;
  /**
   * Right yAxis options which are relevant when a series is overridden to use the right axis.
   */
  yAxisRight?: TimeSeriesChartYAxisOptions;
  /**
   * Filter fuction for series search, used along with quickFilter.
   */
  filterSeriesFn: UseTimeSeriesQuickFilterResponse['filterSeriesFn'];
  /**
   * Array of renderSeriesIcon callbacks, each returning a custom legend icon for a series.
   */
  renderSeriesIconFns?: Array<QueryMetadata['renderSeriesIcon']>;
  /**
   * Used for alternate handling of the legend when "group by" functionality is enabled.
   */
  timeSeriesGrouping?: TimeSeriesGroup;
  /**
   * Determines if the legend should be rendered using "group by" or not.
   */
  isGrouped?: boolean;
  /**
   * Configuration for grouping legend items.
   */
  getGroupLegendItem?: TimeSeriesGroupByProps['getGroupLegendItem'];
  /**
   * Common labels that will be displayed in the tooltip. When defined, the tooltip
   * uses `condensedName` (with common labels removed) instead of the full series name.
   * Should be undefined when common labels are not shown (e.g. single tooltip mode).
   */
  commonLabels?: Record<string, string>;
}

export interface TimeSeriesConfigResult {
  /**
   * ECharts series options which control visual display and use datasetIndex to point to processed query results.
   * See: https://apache.github.io/echarts-handbook/en/concepts/dataset/#how-to-reference-several-datasets
   */
  timeSeriesMapping: TimeChartSeriesMapping;
  /**
   * Series to show in custom legend, can be toggled on / off to show and hide.
   */
  legendItems: LegendItem[];
  /**
   * Number of data points not rendered for performance (user can opt in to see all).
   * Related to to optimized mode TIME_SERIES_SPARSE_SERIES_RENDER_LIMIT.
   */
  hiddenDataPointCount: number;
  /**
   * Lookup info about a series by id, used in places like custom tooltip for data links.
   */
  getSeriesById?: GetSeriesById;
  /**
   * Lower level chart has customizations that apply when right y axis is used, defining here
   * avoids an extra loop through all series.
   */
  anySeriesUsingRightAxis?: boolean;
  /**
   * Needed for the tooltip to translate a point to the coordinates of that axis.
   * -1 corresponds to default state when no series are overriden to use the right y axis.
   */
  rightYAxisIndex?: number;
  /**
   * Used for LOG_SCALE_AXIS_NEAR_ZERO_NUMBER correction when log yAxis is active.
   */
  datasetsUsedOnLogAxis?: number[];
  /**
   * Used to translate legend values into columns for the table legend.
   */
  labelKeys?: string[];
  /**
   * When true, the result has labels. This helps us determine how to handle some edge case handling
   * when "labels-to-columns" is enabled but there are no labels.
   */
  hasLabels?: boolean;
}

export function useTimeSeriesConfig(params: TimeSeriesConfigParams): TimeSeriesConfigResult {
  const {
    timeSeriesDataset,
    totalSeries,
    totalDatapoints,
    shouldShowAllDataPoints,
    visual = {},
    enableLabelsToColumns,
    legend,
    selectedLegendItems,
    quickFilter,
    thresholds,
    overrides,
    queryTruncationSeriesLimit,
    isAxisLogScaleMapping = [],
    barCategoryGap,
    yAxis,
    yAxisRight,
    filterSeriesFn,
    getCustomLegendItemData,
    renderSeriesIconFns,
    timeSeriesGrouping,
    isGrouped,
    getGroupLegendItem,
    commonLabels,
  } = params;

  const chartsTheme = useChartsTheme();
  const { theme } = useTheme();

  const seriesColorGenerator = useSeriesColorGenerator();

  const {
    timeSeriesMapping,
    legendItems,
    getSeriesById,
    anySeriesUsingRightAxis,
    rightYAxisIndex,
    datasetsUsedOnLogAxis,
    labelKeys,
    hasLabels,
    hiddenDataPointCount,
    // eslint-disable-next-line react-hooks/preserve-manual-memoization
  } = useMemo(() => {
    const legendItems: LegendItem[] = [];

    // Used to get raw series data from series index (e.g in tooltip).
    // All values are not included since we can use datasetIndex and closestTimestampIndex to look up
    // the TimeSeriesValueTuple that is closest to where the user is hovering.
    const seriesByIdMap = new Map<string, SeriesByIdMapValue>();
    const getSeriesById = (id: string): TimeSeriesWithMapping | undefined => seriesByIdMap.get(id);

    // Used for rendering grouped legend when "group by" functionality is enabled.
    const groupedLegendItems: Record<string, LegendItem[]> = {};

    const timeSeriesMapping: TimeChartSeriesMapping = [];

    // Collect all unique labels, so we can turn them into columns when enableLabelsToColumns is true.
    const labelKeysSet = new Set<string>();

    let anySeriesUsingRightAxis = false;
    let rightYAxisIndex = -1;

    let sparseSeriesCount = 0;
    let hiddenDataPointCount = 0;
    // Array of series indexes that are using a log axis
    // We can use this later on to correct zero values for those series
    const datasetsUsedOnLogAxis: number[] = [];

    // Mapping of each set of processed query results (our custom datasetResponse) to be ECharts option compatible
    // and return required properties for our custom tooltip/legend.
    for (let datasetIndex = 0; datasetIndex < timeSeriesDataset.length; datasetIndex++) {
      let timeSeries = timeSeriesDataset[datasetIndex];
      if (timeSeries === undefined) {
        return { timeSeriesMapping: EMPTY_SERIES_MAPPING, legendItems: EMPTY_LEGEND_ITEMS, hiddenDataPointCount: 0 };
      }

      // Per-query threshold: when the runtime wrapper has tagged this series
      // with `config.threshold`, force the threshold color, render as a dashed
      // reference line, opt out of stack/area, and silence the series in legend.
      const queryThreshold = timeSeries.config?.threshold;
      const isQueryThresholdSeries = queryThreshold !== undefined;

      // Avoids issues with ECharts types
      const rawFormattedName = `${timeSeries.formattedName ?? timeSeries.name}`;
      const formattedSeriesName =
        isQueryThresholdSeries && queryThreshold?.label?.length ? queryThreshold.label : rawFormattedName;
      // Only use condensedName when common labels are displayed in the tooltip,
      // otherwise labels would be removed with no way for the user to see them.
      const tooltipSeriesName = commonLabels ? (timeSeries.condensedName ?? formattedSeriesName) : formattedSeriesName;
      const seriesId = `${timeSeries.id}`;

      // Color is used for line, tooltip, and legend
      let seriesColor: string | undefined;

      let onRightYAxis = false;

      // Check for explicit series overrides.
      const overriddenProperties = getOverriddenProperties<TimeSeriesVisualOverride>(formattedSeriesName, overrides);
      if (overriddenProperties.color) {
        seriesColor = overriddenProperties.color;
      }

      // If no overrides, use `color` property provided by time series if available, otherwise fallback to getSeriesColor
      if (seriesColor === undefined) {
        seriesColor =
          timeSeries.config?.color ??
          seriesColorGenerator.getSeriesColor({
            kind: visual?.colorPalette,
            primaryColor: theme.palette.primary.main,
            seriesName: formattedSeriesName,
            seriesIndex: datasetIndex,
          });
      }

      // Threshold color is honored unless a per-series override has explicitly
      // set a color, in which case the panel-level override wins.
      if (queryThreshold && overriddenProperties.color === undefined) {
        seriesColor = queryThreshold.color;
      }

      const legendCalculations = legend?.values ? getCalculations(timeSeries.source, legend.values) : undefined;

      // Only include the labels in the data we send to the legend when we intend to show them.
      const legendLabelData = enableLabelsToColumns ? { ...timeSeries.labels } : {};

      if (enableLabelsToColumns && timeSeries.labels) {
        for (const labelKey in timeSeries.labels) {
          labelKeysSet.add(labelKey);
        }
      }

      const showSeriesInLegend = filterSeriesFn(timeSeries, quickFilter, { enableLabelsToColumns });
      const seriesSelectedId =
        isGrouped && timeSeries.groupKey
          ? // When grouped, check for the group key
            timeSeries.groupKey
          : // Otherwise, check for the series id.
            seriesId;
      const isSeriesSelected = isLegendItemSelected(
        {
          id: seriesSelectedId,
        },
        selectedLegendItems
      );
      const showSeriesInChart = isSeriesSelected && showSeriesInLegend;

      if (overriddenProperties) {
        onRightYAxis = overriddenProperties.y_axis === 'right';
        if (onRightYAxis && showSeriesInChart) {
          anySeriesUsingRightAxis = true;
          rightYAxisIndex = datasetIndex;

          timeSeries = produce(timeSeries, (draft) => {
            draft.config = { ...(timeSeries?.config || {}), unit: yAxisRight?.unit ?? DEFAULT_UNIT };
          });
        }
      }

      if (showSeriesInChart) {
        const nonconnectedDataPoints = timeSeries.nonconnectedDataPoints ?? EMPTY_NONCONNECTED_DATAPOINTS;
        const isSparseSeries = nonconnectedDataPoints.length > 0;
        if (isSparseSeries) {
          sparseSeriesCount++;
          if (!shouldShowAllDataPoints && sparseSeriesCount >= TIME_SERIES_SPARSE_SERIES_RENDER_LIMIT) {
            hiddenDataPointCount += nonconnectedDataPoints.length;
          }
        }

        // If the series is compared to another series, style it appropriately
        if (timeSeries.compareToSeriesId) {
          const compareToSeriesColor = seriesByIdMap.get(timeSeries.compareToSeriesId)?.config?.color;
          if (compareToSeriesColor !== undefined) {
            seriesColor = compareToSeriesColor;
          }
        }

        // Each series is stored as a separate dataset source.
        // https://apache.github.io/echarts-handbook/en/concepts/dataset/#how-to-reference-several-datasets
        timeSeriesMapping.push(
          getTimeSeries({
            id: seriesId,
            datasetIndex,
            formattedSeriesName: tooltipSeriesName,
            visual,
            seriesColor,
            lineStyle: overriddenProperties.line_style,
            lineWidth: overriddenProperties.line_width,
            nonconnectedDataPoints,
            sparseSeriesCount,
            totalSeries,
            totalDatapoints,
            onRightYAxis,
            barCategoryGap,
            isComparedSeries: timeSeries.config?.compareTo !== undefined,
            queryTruncationSeriesLimit,
            shouldShowAllDataPoints,
            chartsTheme,
            excludeFromStack: isQueryThresholdSeries,
            isQueryThresholdSeries,
          })
        );

        // If the series is using a log axis, add it to the list
        const yAxisIndex = timeSeriesMapping[timeSeriesMapping.length - 1]?.yAxisIndex ?? 0;
        if (isAxisLogScaleMapping[yAxisIndex] || false) {
          datasetsUsedOnLogAxis.push(datasetIndex);
        }
      }

      const queryResultIndex = timeSeries.queryResultIndex ?? 0;

      const timeSeriesWithoutValues: TimeSeriesWithoutValues = {
        name: formattedSeriesName,
        labels: timeSeries.labels,
        config: { color: seriesColor, ...timeSeries.config },
      };

      // Set the raw series by index so we can get it later in the tooltip.
      // Include index to lookup all options from seriesMapping and which
      // query the series came from.
      seriesByIdMap.set(seriesId, {
        ...timeSeriesWithoutValues,
        datasetIndex,
        mappingIndex: timeSeriesMapping.length - 1,
        // TODO: rename queryResultsIndex to queryResultIndex in tooltip
        queryResultsIndex: queryResultIndex,
      });

      // Filter out truncation shaded band series from legend (but keep it plotted on the chart).
      const isDiffBandSeries = formattedSeriesName === DIFF_BAND_TRUNCATION_SERIES_NAME;

      if (legend && showSeriesInLegend && !isDiffBandSeries && !isQueryThresholdSeries) {
        const legendCustomData = getCustomLegendItemData?.(timeSeries);

        const legendItem: LegendItem = {
          id: seriesId, // Avoids duplicate key console errors when there are duplicate series names
          label: onRightYAxis ? `${formattedSeriesName} (right y-axis)` : formattedSeriesName,
          color: seriesColor,
          data: { ...legendCalculations, ...legendLabelData, ...legendCustomData },
          unit: onRightYAxis ? yAxisRight?.unit : undefined,
          icon:
            renderSeriesIconFns?.[queryResultIndex] && renderSeriesIconFns[queryResultIndex]?.(timeSeriesWithoutValues),
          isComparison: timeSeries.config?.compareTo !== undefined,
        };

        if (isGrouped) {
          // When grouping, collect the list of legend items, so they can be processed into a single
          // group legend item.
          if (timeSeries.groupKey) {
            if (!groupedLegendItems[timeSeries.groupKey]) {
              groupedLegendItems[timeSeries.groupKey] = [];
            }
            groupedLegendItems[timeSeries.groupKey]!.push(legendItem);
          }
        } else {
          // When not grouped, use the legend item as is.
          legendItems.push(legendItem);
        }
      }
    }

    if (thresholds && thresholds.steps) {
      // Convert how thresholds are defined in the panel spec to valid ECharts markLine and markArea options.
      // These are styled with predefined colors and a dashed line or area style to look different than normal TimeSeries from query results.
      // There will only ever be one threshold series since each step value is stored in the appropriate data array.
      const thresholdSeriesName = `${THRESHOLD_SERIES_NAME_PREFIX} ${timeSeriesMapping.length}`; // Add prefix in case we need to lookup by series name in tooltip.
      const thresholdColors = chartsTheme.thresholds;
      const thresholdSeries = getThresholdsSeries({
        thresholds,
        thresholdColors,
        name: thresholdSeriesName,
        dataset: timeSeriesDataset,
        yAxisMax: yAxis?.max,
      });
      if (thresholdSeries) {
        timeSeriesMapping.push(thresholdSeries);
      }
    }

    // When grouped, iterate the groupings and generate legend items to show that represent the
    // groups.
    if (isGrouped && groupedLegendItems && timeSeriesGrouping && legend) {
      for (const groupKey in timeSeriesGrouping) {
        const timeSeriesGroup = groupedLegendItems[groupKey];
        // If there's no items, do not show the group. This is useful for cases with filtering.
        if (timeSeriesGroup?.length) {
          const partialLegendItem = getGroupLegendItem?.(timeSeriesGroup);

          legendItems.push({
            ...partialLegendItem,
            id: groupKey,
            label: partialLegendItem?.label ?? groupKey,
            // We use the primary palette color for groups to make it clear they don't directly map
            // to an entry in the chart (similar to the select all/none in the header).
            color: theme.palette.text.primary,
          });
        }
      }
    }

    return {
      timeSeriesMapping,
      legendItems,
      getSeriesById,
      anySeriesUsingRightAxis,
      rightYAxisIndex,
      datasetsUsedOnLogAxis,
      labelKeys: Array.from(labelKeysSet),
      hasLabels: labelKeysSet.size > 0,
      hiddenDataPointCount,
    };
  }, [
    thresholds,
    timeSeriesDataset,
    overrides,
    legend,
    selectedLegendItems,
    yAxisRight?.unit,
    seriesColorGenerator,
    visual,
    theme.palette.primary.main,
    totalSeries,
    totalDatapoints,
    chartsTheme,
    yAxis?.max,
    barCategoryGap,
    queryTruncationSeriesLimit,
    isAxisLogScaleMapping,
    quickFilter,
    filterSeriesFn,
    enableLabelsToColumns,
    getCustomLegendItemData,
    shouldShowAllDataPoints,
    renderSeriesIconFns,
    getGroupLegendItem,
    isGrouped,
    timeSeriesGrouping,
    theme.palette.text.primary,
    commonLabels,
  ]);

  return {
    timeSeriesMapping,
    legendItems,
    hiddenDataPointCount,
    getSeriesById,
    anySeriesUsingRightAxis,
    rightYAxisIndex,
    datasetsUsedOnLogAxis,
    labelKeys,
    hasLabels,
  };
}
